import React, { useState, useEffect } from 'react';
import {
  Button,
  Typography,
  Box,
  Card,
  CardMedia,
  MenuItem,
  Menu,
  Divider,
  MenuList,
  Chip,
  Avatar as MUIAvatar,
  IconButton,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Pencil } from 'lucide-react';
import { SkillLevelsArraySchema } from '../../types';
import { Instruments } from '@/shared/i18n';
import { LevelIconSelector } from '../../utils/select-level-icon';
import { SkilllevelsType } from 'src/shared/types/course';

interface InstrumentCardProps {
  imageSrc: string;
  name: string;
  onDelete: () => void;
  skillLevel?: string;
  skillLevelsOptions: SkillLevelsArraySchema;
  onUpdate: () => void;
  dict: Instruments;
}

const InstrumentAddedCard: React.FC<InstrumentCardProps> = ({
  imageSrc,
  name,
  onDelete,
  skillLevel,
  onUpdate,
  dict,
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  // Handlers
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (anchorEl && !anchorEl.contains(event.target as Node)) {
        handleMenuClose();
      }
    }

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open, anchorEl]);

  return (
    <Card className="flex flex-wrap items-center bg-paper radius-xl w-full lg:min-h-[120px] px-6 py-4">
      <CardMedia
        component="img"
        image={imageSrc}
        alt={`${name} instrument`}
        sx={{ objectFit: 'contain', aspectRatio: '1/1' }}
        className="p-0 w-[48px] md:w-[72px] rounded-none"
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'start',
          flex: 1,
          paddingLeft: 2,
        }}
      >
        <Typography variant="h5" component="p" className="mb-2">
          {name}
        </Typography>

        {/* Skill Level */}
        <Chip
          label={
            <Typography variant="button-s" className="capitalize">
              {skillLevel}
            </Typography>
          }
          avatar={
            <MUIAvatar
              src={LevelIconSelector(skillLevel?.toLowerCase() as SkilllevelsType)}
              className="mr-0"
              sx={{ borderRadius: 0 }}
            />
          }
          size="small"
          variant="filled"
          sx={{
            backgroundColor: theme.palette.neutral.main,
            color: theme.palette.primary.contrastText,
          }}
        />
      </Box>

      {/* Menu */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button
          variant="text"
          size="medium"
          color="primary"
          onClick={handleMenuOpen}
          className="hidden sm:block"
        >
          <Typography variant="button-m" className="capitalize">
            {dict.instruments.change_instrument}
          </Typography>
        </Button>
        <IconButton color="primary" onClick={handleMenuOpen} className="sm:hidden">
          <Pencil height="20px" className="sm:hidden" />
        </IconButton>

        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleMenuClose}
          className="shadow-none"
          MenuListProps={{ 'aria-labelledby': 'edit-button' }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <MenuList className="border rounded-lg shadow-none border-action-divider">
            <MenuItem
              onClick={() => {
                handleMenuClose();
                onUpdate();
              }}
              className="py-2"
            >
              <Typography variant="button-l" color="textPrimary">
                {dict.instruments.change_auto_evaluation}
              </Typography>
            </MenuItem>
            <Divider />
            <MenuItem onClick={onDelete} className="text-error py-2">
              <Typography variant="button-l" color="error">
                {dict.instruments.delete_instrument}
              </Typography>
            </MenuItem>
          </MenuList>
        </Menu>
      </Box>
    </Card>
  );
};

export default InstrumentAddedCard;
